import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getSportsDataAction } from "../../features/sports/sportsSlice";
import { getBalanceAndPin } from "../../features/balance-and-pin/balanceAndPinSlice";
import { getDasDeviceInfo } from "../../features/das-device-manager/dasDeviceManagerSlice";

import usePrevious from "../../hooks/usePrevious";
import YourKeyfob from "./YourKeyfob";
import Payments from "./Payments";
import Memories from "./Memories";
import RolandGarrosInfo from "./RolandGarrosInfo";
import Spinner from "../../components/Spinner";
import Court from "./Court";
import { getVisibleCourts } from "./courtUtils";

import {
  MainContainer,
  NavBar,
  IntroContainer,
  DiscoverContainer,
  FooterContainer,
  Separator,
  ScheduleItem,
  DiscoverItem
} from "./styles";

import logo from "./imgs/nav-logos.svg";
import rolandGarros from "./imgs/roland-garros.webp";
import yourKeyfob from "./imgs/your-keyfob.webp";

import memoriesRolandGarros from "./imgs/memories.webp";
import poweredBy from "./imgs/digiseq-logo.svg";
import paymentServices from "./imgs/payme-swiss.webp";
import copyIconWhite from "./imgs/copy-icon-white.svg";
import packageJson from "../../../package.json";

const RolandGarros25 = () => {
  const [searchParams] = useSearchParams();
  const serialNumber = searchParams.get("serialNumber");
  const eventId = searchParams.get("eventId");

  const [copied, setCopied] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const dispatch = useDispatch();
  const { balanceAndPin, isLoading } = useSelector(
    (state) => state.balanceAndPin
  );
  const { dasDeviceInfo, gettingDasDeviceInfo, errorGettingDasDeviceInfo } =
    useSelector((state) => state.dasDevices);
  const { publicEvent, gettingPublicEvent, errorGettingPublicEvent } =
    useSelector((state) => state.publicEvents);

  const { sportsData, gettingSportsData, errorGettingSportsData } = useSelector(
    (state) => state.sports
  );

  const { activatingTicket, activatingTicketError } = useSelector(
    (state) => state.hugoBoss
  );

  const prevActivatingTicket = usePrevious(activatingTicket);

  useEffect(() => {
    dispatch(
      getSportsDataAction({
        sportId: "13",
        leagueId: ["10222", "15850"]
      })
    );
  }, [dispatch]);

  const getSelectedOptionId = (fieldId) => {
    return dasDeviceInfo?.eventData?.[
      eventId
    ]?.infoCollector?.selectedChoices?.find((item) => item.fieldId === fieldId)
      ?.selectedOptionId;
  };

  const getHighlights = (selectedOptionId) => {
    if (
      !publicEvent?.infoCollectorEventData?.fields[0]?.options[0]
        ?.additionalData?.highlights
    )
      return null;

    const groupData = publicEvent?.infoCollectorEventData?.fields?.filter(
      (item) => item?.id === "group"
    )[0];

    const findOptions = groupData?.options?.find(
      (item) => item?.id === selectedOptionId
    );

    if (findOptions?.additionalData?.highlights) {
      return findOptions.additionalData.highlights;
    }

    return null;
  };

  useEffect(() => {
    if (prevActivatingTicket && !activatingTicketError) {
      dispatch(getDasDeviceInfo(serialNumber));
    }
  }, [prevActivatingTicket, activatingTicketError, dispatch, serialNumber]);

  useEffect(() => {
    setIsMounted(true);

    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    if (isMounted) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied, isMounted]);

  useEffect(() => {
    if (!balanceAndPin && serialNumber) {
      dispatch(
        getBalanceAndPin({
          serial: serialNumber,
          query: "?provider=pannovate"
        })
      );
    }
  }, [balanceAndPin, serialNumber, dispatch]);

  useEffect(() => {
    if (serialNumber) {
      dispatch(getDasDeviceInfo(serialNumber));
    }
  }, [serialNumber, dispatch]);

  const [isMemoriesOpen, setIsMemoriesOpen] = useState(false);

  const [isRolandGarrosOpen, setIsRolandGarrosOpen] = useState(false);
  const [isYourKeyfobOpen, setIsYourKeyfobOpen] = useState(false);
  const [isPaymentsOpen, setPaymentsOpen] = useState(false);

  const handleClose = (closeItem) => {
    const discoverContainer = document.getElementById("discover");
    discoverContainer.scrollIntoView({ behavior: "smooth" });

    switch (closeItem) {
      case "memories":
        setIsMemoriesOpen(false);
        break;
      case "payments":
        setPaymentsOpen(false);
        break;
      case "yourKeyfob":
        setIsYourKeyfobOpen(false);
        break;
      case "rolandGarros":
        setIsRolandGarrosOpen(false);
        break;
      default:
        break;
    }
  };

  const handleOpen = (openItem) => {
    //scroll to top
    window.scrollTo(0, 0);
    switch (openItem) {
      case "memories":
        setIsMemoriesOpen(true);
        break;
      case "payments":
        setPaymentsOpen(true);
        break;
      case "yourKeyfob":
        setIsYourKeyfobOpen(true);
        break;
      case "rolandGarros":
        setIsRolandGarrosOpen(true);
        break;
      default:
        break;
    }
  };
  const parseHighlights = (highlights) => {
    const parsedHighlights = JSON.parse(highlights);

    return parsedHighlights;
  };

  const renderHighlights = () => {
    const selectedOptionId = getSelectedOptionId("group");

    if (!selectedOptionId) return null;
    const highlights = getHighlights(selectedOptionId);

    if (!highlights) return null;

    return (
      <>
        {parseHighlights(highlights)
          .filter(
            (item) => item.date === new Date().toISOString().split("T")[0]
          )
          .map((item, index) => {
            const formattedDate = new Date(item.date).toLocaleDateString(
              "en-GB",
              {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric"
              }
            );
            return (
              <ScheduleItem key={index}>
                <div className="schedule-list">
                  <p>{formattedDate}</p>
                  <Separator />

                  {item?.schedule.map((scheduleItem, indexSchedule) => {
                    return (
                      <p className="schedule-item" key={indexSchedule}>
                        <span className="time">
                          {scheduleItem.time ? scheduleItem.time : ""}
                        </span>
                        {scheduleItem.time ? " - " : ""}
                        <span className={!scheduleItem.time ? "time" : ""}>
                          {scheduleItem.title}
                        </span>
                      </p>
                    );
                  })}
                </div>
              </ScheduleItem>
            );
          })}
      </>
    );
  };

  if (
    isLoading ||
    gettingDasDeviceInfo ||
    gettingPublicEvent ||
    gettingSportsData
  )
    return <Spinner />;

  return (
    <MainContainer>
      <NavBar>
        <img src={logo} alt="logo" />
      </NavBar>
      <IntroContainer>
        <div className="image-container">
          {dasDeviceInfo?.personalDetails?.firstName && (
            <h1>
              {"Welcome,"}{" "}
              <span className="new-line">
                {dasDeviceInfo?.personalDetails?.firstName}{" "}
                {dasDeviceInfo?.personalDetails?.lastName}
              </span>
            </h1>
          )}
        </div>

        <div className="text-container">
          <h2>{"Welcome!"}</h2>
          <p>
            We're happy to welcome you to Roland-Garros, where Mastercard has
            been official payment partner for 12 years. We're delighted that you
            are joining us to witness tennis legends leave their mark on the
            iconic clay.
          </p>
        </div>
      </IntroContainer>

      {getVisibleCourts(sportsData).map((court, index) => (
        <Court
          key={court.courtName}
          courtName={court.courtName}
          matches={court.matches}
          isOdd={index % 2 === 0}
          isExpanded={index === 0}
        />
      ))}

      <DiscoverContainer id="discover">
        <h2>{"Your experience"}</h2>
        <p>{"Tap to find out what there is to discover"}</p>
        <div className="items-container">
          <DiscoverItem onClick={() => handleOpen("payments")}>
            <img src={yourKeyfob} alt="payments" />
          </DiscoverItem>
          <DiscoverItem onClick={() => handleOpen("rolandGarros")}>
            <img src={rolandGarros} alt="your stay" />
          </DiscoverItem>
          <DiscoverItem onClick={() => handleOpen("memories")}>
            <img src={memoriesRolandGarros} alt="memories" />
          </DiscoverItem>
        </div>
      </DiscoverContainer>

      <FooterContainer>
        <h3>{"Powered by"}</h3>
        <img src={poweredBy} alt="powered by" />
        <h3>{"Payment services by"}</h3>
        <img src={paymentServices} alt="payment services" />
        <p>
          <a href="https://www.mastercard.com/global/en.html">
            {"Privacy Policy"}
          </a>
        </p>
        <p>
          <a href="https://www.mastercard.com/global/en.html">
            {"Terms & Conditions"}
          </a>
        </p>
        {serialNumber && (
          <div className="copyToClipboard">
            <p>ID: {serialNumber}</p>
            <img
              onClick={() => {
                navigator.clipboard.writeText(serialNumber);
                setCopied(true);
              }}
              src={copyIconWhite}
              alt="copy to clipboard"
            />
          </div>
        )}
        <div className="version">
          <p>v.{packageJson.version}</p>
        </div>
        {copied && (
          <p>
            <b>Copied to clipboard</b>
          </p>
        )}
      </FooterContainer>
      {isMemoriesOpen && (
        <Memories
          serialNumber={serialNumber}
          onClose={() => handleClose("memories")}
          eventName={"CANNES"}
        />
      )}
      {isPaymentsOpen && (
        <Payments
          onClose={() => handleClose("payments")}
          remainingBalance={balanceAndPin?.balanceDetails?.availableBalance}
          activationCode={balanceAndPin?.activationCode}
        />
      )}
      {isYourKeyfobOpen && (
        <YourKeyfob onClose={() => handleClose("yourKeyfob")} />
      )}

      {isRolandGarrosOpen && (
        <RolandGarrosInfo onClose={() => handleClose("rolandGarros")} />
      )}
    </MainContainer>
  );
};

export default RolandGarros25;
